import { useEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "~/lib/auth";
import {
  GUEST_PROGRESS_QUERY_KEY,
  GUEST_STARTER_QUERY_KEY,
  GUEST_TIMERS_QUERY_KEY,
} from "~/lib/guest-data";

const GUEST_QUERY_KEY_PREFIXES = new Set<unknown>([
  GUEST_STARTER_QUERY_KEY[0],
  GUEST_PROGRESS_QUERY_KEY[0],
  GUEST_TIMERS_QUERY_KEY[0],
]);

/**
 * Drops cached authenticated data when the signed-in user changes (sign out,
 * or signing in as someone else on the same device) so the next screen never
 * flashes the previous user's starter, timers, or progress. The guest data is
 * exempt — it's meant to survive sign-out.
 */
export function QuerySessionBoundary({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const previousUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const nextUserId = user?.id ?? null;
    if (
      previousUserId.current !== undefined &&
      previousUserId.current !== nextUserId
    ) {
      queryClient.resetQueries({
        predicate: (query) => !GUEST_QUERY_KEY_PREFIXES.has(query.queryKey[0]),
      });
    }
    previousUserId.current = nextUserId;
  }, [queryClient, user?.id]);

  return children;
}
